import { useAuthStore } from '~/stores/auth.store';
import { useSiteStore } from '~/stores/site.store';
import { useRgStore } from '~/stores/responsibleGaming.store';
import { useAddMarketingSession } from '../useAddMarketingSession';
import type {
  IEditUserRequest,
  ILoginRequest,
  IPartialRegStepOneRequest,
  IRegisterRequest,
  IResetPasswordRequest,
  IVerifyPartialRegOTPRequest,
  IAddLimitRequest,
  IRemoveLimitRequest,
  IBankingAccountSettingsRequest,
  ICommunicationPreferencesRequest,
} from '~/interfaces/dto/auth/request';
import type {
  IForgotLoginInitResponse,
  ILoginResponse,
  IMobileNumbersResponse,
  IPartialRegStepOneResponse,
  IRegisterResponse,
  IUserInfoExtended,
  IUserInfoExtendedResponse,
  IVerifyPartialRegOTPResponse,
  IAddLimitResponse,
  IBankingAccountSettingsResponse,
  IBrandGetLimitsType,
  IUserLimits,
  IUserGetLimitsType,
  IWagerProgress,
} from '~/interfaces/dto/auth/response';
import type {
  IRewardsRedeemResponse,
  IRewardsConfigResponse,
  IRewardsBalanceResponse,
  IPeriod,
} from '~/interfaces/dto/rewards/response';
import type { IConvertToCashRequest } from '~/interfaces/dto/rewards/request';

import type { IBaseResponse } from '~/interfaces/dto/base/request';
import { globalKeys } from 'assets/data/global-keys';

export const useAuthService = () => {
  const authStore = useAuthStore();
  const siteStore = useSiteStore();
  const rgStore = useRgStore();

  const { $fetch } = useNuxtApp();

  const internalNote = 'jpc-web';

  // register
  const initPartialReg = async (body: IPartialRegStepOneRequest) => {
    try {
      return await $fetch<IPartialRegStepOneResponse>(
        'v3/Users/register-partial',
        {
          base: 'authApi',
          method: 'post',
          body,
          useBrandId: true,
        },
      );
    } catch (e) {
      console.error(e);
      throw e;
    }
  };
  const verifyPartialRegOtp = async (body: IVerifyPartialRegOTPRequest) => {
    try {
      return await $fetch<IVerifyPartialRegOTPResponse>(
        'v3/Contact/verify/otp-registration',
        {
          base: 'authApi',
          method: 'post',
          body,
          useBrandId: true,
        },
      );
    } catch (e) {
      console.error(e);
      throw e;
    }
  };
  const requestPartialRegOtp = async (username: string) => {
    try {
      return await $fetch<IBaseResponse>(
        'v3/Contact/request/otp-partial-registration',
        {
          method: 'post',
          base: 'authApi',
          body: {},
          query: { username },
          useBrandId: true,
        },
      );
    } catch (e) {
      console.error(e);
      throw e;
    }
  };
  const completePartialReg = async (body: IRegisterRequest) => {
    try {
      return await $fetch<IRegisterResponse>(
        `v3/Users/complete-partial-registration`,
        {
          base: 'authApi',
          method: 'post',
          body,
          useAuthentication: true,
          useBrandId: true,
        },
      );
    } catch (e) {
      console.error(e);
      throw e;
    }
  };
  const register = async (
    request: IRegisterRequest,
  ): Promise<IRegisterResponse> => {
    try {
      const { executeRecaptcha } = await useRecaptcha();
      const { username, password } = request;
      const versionNumber = !!request?.recaptcha ? 'v3' : 'v2';

      return await $fetch<IRegisterResponse>(
        `${versionNumber}/Users/register`,
        {
          base: 'authApi',
          method: 'post',
          body: request,
          useBrandId: true,
          async onResponse({ response }) {
            if (!!response._data && response?._data?.isSuccessful) {
              await login(
                {
                  username,
                  password,
                  ...(!!request.recaptcha && {
                    recaptcha: await executeRecaptcha('login'),
                  }),
                },
                true,
              ).then(() => {
                siteStore.deactivateModal();
              });
            } else {
              console.error(response?._data?.error);
            }
            return response._data;
          },
        },
      );
    } catch (e) {
      console.error(e);
      throw e;
    }
  };
  // login / session
  const login = async (
    body: ILoginRequest,
    isAssociatedToReg: boolean = false,
  ): Promise<ILoginResponse> => {
    const { $marketingService } = useServices();
    const res = ref<ILoginResponse>();
    try {
      const versionNumber = !!body?.recaptcha ? 'v3' : 'v2';

      await $fetch<ILoginResponse>(`${versionNumber}/Users/authenticate`, {
        base: 'authApi',
        method: 'post',
        body,
        useBrandId: true,
        async onResponse({ response }) {
          if (!!response._data.data?.jwtToken) {
            const { jwtToken, refreshToken, id } = response._data.data;
            await $marketingService.publishPlatformMarketingEvent({
              type: isAssociatedToReg ? 'registrationCompleted' : 'login',
              username: body.username,
              jwtToken,
              id,
            });
            authStore.setAccessToken(jwtToken);
            authStore.setRefreshToken(refreshToken);
            authStore.setUserId(id);
            await Promise.allSettled([
              await authStore.setLoggedIn(),
              await useAddMarketingSession(),
            ]);
          }
          res.value = response._data;
          return response._data;
        },
      });

      return res.value;
    } catch (e) {
      console.error(e);
      return e;
    }
  };
  const handleLoginSession = async (queryStr: string) => {
    const refreshTokenStr: ILoginResponse['data']['refreshToken'] = JSON.parse(
      atob(queryStr),
    );
    authStore.setRefreshToken(refreshTokenStr);

    await Promise.allSettled([
      await refreshToken(),
      await authStore.setLoggedIn(),
    ]);
  };
  const refreshToken = async () => {
    try {
      if (!authStore.currentRefreshToken) return;
      return await $fetch('v3/Users/refresh-token', {
        base: 'authApi',
        method: 'post',
        body: {},
        useBrandId: true,
        headers: {
          'Refresh-Token': `${authStore.currentRefreshToken}`,
        },
        onResponse({ response }) {
          if (!!response._data.error) authStore.logout();
          const accessToken = response._data?.data?.jwtToken;
          const refreshToken = response._data?.data?.refreshToken;
          authStore.setAccessToken(accessToken);
          authStore.setRefreshToken(refreshToken);
        },
        onRequestError() {
          authStore.logout();
        },
        onResponseError() {
          authStore.logout();
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
  // user info
  const getUserExtended = async () => {
    const { $enabled } = useNuxtApp();
    const route = useRoute();
    try {
      return await $fetch<IUserInfoExtendedResponse>(
        'v3/Users/user-info-extended',
        {
          base: 'authApi',
          useAuthentication: true,
          useBrandId: true,
          onResponse({ response }) {
            if (siteStore.isWebview) return;
            const res = response?._data?.data;
            authStore.setUser(res);
            if ($enabled('myaccount.responsiblegaming')) {
              if (res?.lockedStatus) {
                rgStore.setAccountLockStatus(res.lockedStatus);
              }
            }
            if ($enabled('myaccount.enablecompliance')) {
              //set user compliant status for red dot alert on profile and doc-ver
              if (res?.compliance) {
                authStore.setPartialPostReg(
                  res?.compliance?.complianceStatus < 1,
                ); //If not Fica'd set partial post reg to true - this is to cover user refreshes during post reg
              }
              //Check if deceased or minor and log them out
              if (
                res?.compliance.hasOwnProperty('isValidId') &&
                !res?.compliance?.isValidId &&
                res?.compliance?.complianceStatus <
                  res?.compliance?.requiredComplianceStatus
              ) {
                authStore.logout();
              }
            }

            nextTick(() => {
              if (response._data.isSuccessful) {
                const isRegComplete = res?.isRegistrationComplete;
                const isDocumentVerificationRequired =
                  !authStore.getIsUserCompliant;
                const isResponsibleGamingOpen =
                  authStore.getAccountModalPage === 'responsible-gaming';
                const isAccountRestrictedModalOpen =
                  route.query.hasOwnProperty('accountRestricted') ||
                  siteStore.getCurrentModal === 'accountRestricted';
                rgStore.setPartialRegLock(!isRegComplete);
                if (
                  $enabled('myaccount.enablepartialreg') &&
                  !isRegComplete &&
                  !isAccountRestrictedModalOpen &&
                  (!isRegComplete || isDocumentVerificationRequired)
                ) {
                  if (!isRegComplete) {
                    if (
                      authStore.getAccountModalPage ===
                        'document-verification' &&
                      !rgStore.hasPartialRegLock
                    ) {
                      authStore.toggleAccountModal(false);
                    }
                    if (
                      !isResponsibleGamingOpen &&
                      !isAccountRestrictedModalOpen &&
                      rgStore.hasPartialRegLock &&
                      !isDocumentVerificationRequired
                    ) {
                      siteStore.activateModal('complete-registration');
                    }
                  } else if (
                    isDocumentVerificationRequired &&
                    isRegComplete &&
                    !isResponsibleGamingOpen &&
                    !isAccountRestrictedModalOpen
                  ) {
                    authStore.setAccountModalPage('document-verification');
                  }
                }
              }
            });
          },
        },
      );
    } catch (e) {
      console.error(e);
    }
  };
  const editUser = async (request: IEditUserRequest) => {
    try {
      return await $fetch<IBaseResponse>('v3/Users/edit-user', {
        base: 'authApi',
        useAuthentication: true,
        useBrandId: true,
        method: 'post',
        body: request,
        onResponse({ response }) {
          if (response?._data?.isSuccessful) authStore.setUser(response._data);
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
  // mobile number
  const addMobileNumber = async (body: { mobileNumber: string }) => {
    const { $enabled } = useNuxtApp();
    const { $storageService } = useServices();
    const token = $enabled('security.highriskeventschangelogin')
      ? $storageService.getSessionStorage({
          key: globalKeys.authKeys.sumSubLastLivenessToken,
        })
      : null;
    try {
      return await $fetch<IBaseResponse>('v3/Contact/mobile-number/add', {
        method: 'post',
        body: { ...body, ...(!!token && { token }) },
        base: 'authApi',
        useBrandId: true,
        useAuthentication: true,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const deleteMobileNumber = async ({ mobileNumber }) => {
    try {
      return await $fetch<IBaseResponse>('v3/Contact/mobile-number/remove', {
        method: 'post',
        base: 'authApi',
        useBrandId: true,
        useAuthentication: true,
        body: {
          mobileNumber,
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
  const changePrimaryNumber = async ({ newPrimaryMobileNumber }) => {
    const { $enabled } = useNuxtApp();
    const { getMobileNumberStatuses } = useMobileNumberHandler();
    const { $storageService } = useServices();
    const token =
      $enabled('security.highriskeventschangelogin') ||
      $enabled('security.highriskeventsprofilemanagement')
        ? $storageService.getSessionStorage({
            key: globalKeys.authKeys.sumSubLastLivenessToken,
          })
        : null;
    try {
      return await $fetch<IBaseResponse>(
        'v3/Contact/mobile-number/primary-change',
        {
          method: 'post',
          base: 'authApi',
          useBrandId: true,
          useAuthentication: true,
          body: {
            newPrimaryMobileNumber,
            ...(!!token && { token }),
          },
          onResponse({ response }) {
            if (response._data.isSuccessful) {
              authStore.setUser({
                ...authStore.user,
                primaryMobileNumber: newPrimaryMobileNumber,
              } as IUserInfoExtended);
              getMobileNumberStatuses();
            }
          },
        },
      );
    } catch (e) {
      console.error(e);
    }
  };
  const requestNewOtp = async ({ mobileNumber }) => {
    try {
      return await $fetch<IBaseResponse>('v3/Contact/resend-otp', {
        method: 'post',
        useBrandId: true,
        base: 'authApi',
        useAuthentication: true,
        body: {
          accountId: authStore.userId,
          mobileNumber,
          contactPreferences: 1,
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
  const verifyOtp = async ({ mobileNumber, otp, isPrimary }) => {
    try {
      return await $fetch<IBaseResponse>('v3/Contact/verify/otp', {
        base: 'authApi',
        useBrandId: true,
        method: 'post',
        useAuthentication: true,
        body: {
          mobileNumber,
          otp,
          isPrimary,
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
  const getMobileNumberStatus = async () => {
    try {
      return await $fetch<IMobileNumbersResponse>('v3/Contact/mobile-numbers', {
        base: 'authApi',
        useBrandId: true,
        useAuthentication: true,
      });
    } catch (e) {
      console.error(e);
    }
  };
  // password functions
  const passwordResetInit = async (body: IResetPasswordRequest) => {
    try {
      return await $fetch<IBaseResponse>('v3/Users/password-change/initiate', {
        method: 'post',
        base: 'authApi',
        useBrandId: true,
        body,
      });
    } catch (e) {
      console.error(e);
    }
  };
  const passwordResetConfirm = async ({
    password,
    login,
    token,
  }: {
    password: string;
    login: string;
    token?: string;
  }) => {
    const { $enabled } = useNuxtApp();
    const { $storageService } = useServices();
    const livenessToken =
      $enabled('security.highriskeventschangelogin') ||
      $enabled('security.highriskeventsprofilemanagement')
        ? $storageService.getSessionStorage({
            key: globalKeys.authKeys.sumSubLastLivenessToken,
          })
        : null;
    try {
      return await $fetch<IBaseResponse>('v3/Users/password-change/confirm', {
        method: 'post',
        base: 'authApi',
        useBrandId: true,
        body: {
          password,
          login,
          ...(livenessToken && { livenessToken }),
          ...(!!token && { token }),
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
  const passwordResetLoggedIn = async ({ password, confirmPassword }) => {
    const { $enabled } = useNuxtApp();
    const { $storageService } = useServices();
    const livenessToken =
      $enabled('security.highriskeventsprofilemanagement') ||
      $enabled('security.highriskeventschangelogin')
        ? $storageService.getSessionStorage({
            key: globalKeys.authKeys.sumSubLastLivenessToken,
          })
        : null;
    try {
      return await $fetch<IBaseResponse>('v3/Users/password-change', {
        method: 'post',
        base: 'authApi',
        useBrandId: true,
        useAuthentication: true,
        body: {
          password,
          confirmPassword,
          ...(!!livenessToken && { livenessToken }),
        },
        async onResponseError({ response }) {
          //return error to throw general error on update password
          return response._data;
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
  const forgotLoginInit = async (body: {
    idNumber: string;
    idType: number;
  }) => {
    try {
      return await $fetch<IForgotLoginInitResponse>(
        'v3/Users/forgot-login/initiate',
        {
          base: 'authApi',
          useBrandId: true,
          method: 'post',
          body,
          async onResponseError({ response }) {
            return response._data;
          },
        },
      );
    } catch (e) {
      console.error(e);
    }
  };
  const forgotLoginAuthorise = async (body: {
    idNumber: string;
    idType: number;
    token: string;
  }) => {
    try {
      return await $fetch<IBaseResponse & { data: string }>(
        'v3/Users/forgot-login/authorise',
        {
          base: 'authApi',
          useBrandId: true,
          method: 'post',
          body,
          async onResponseError({ response }) {
            return response._data;
          },
        },
      );
    } catch (e) {
      console.error(e);
    }
  };

  // account settings
  const fetchCommunicationsPreferences = async () => {
    try {
      return await $fetch<ICommunicationPreferencesRequest>(
        'api/v1/Account/CommunicationPreferences',
        {
          base: 'base',
          useAuthentication: true,
          onResponse: ({ response }) => {
            if (response.status === 404)
              return {
                allowSms: false,
                allowEmail: false,
                allowPushNotification: false,
              };
            else return response._data;
          },
        },
      );
    } catch (e) {
      console.error(e);
    }
  };
  const updateCommunicationsPreferences = async (
    body: ICommunicationPreferencesRequest,
  ) => {
    try {
      return await $fetch('api/v1/Account/CommunicationPreferences', {
        base: 'base',
        method: 'POST',
        useAuthentication: true,
        body,
        onResponse: ({ response }) => {
          return response.ok && response.status === 200;
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
  //reversable withdrawal
  const getBankingAccountSettings = async () => {
    try {
      return await $fetch<IBankingAccountSettingsResponse>(
        'v2/AccountSettings',
        {
          base: 'authApi',
          useBrandId: true,
          useAuthentication: true,
        },
      );
    } catch (e) {
      console.error(e);
    }
  };
  const upsertBankingAccountSettings = async (
    request: IBankingAccountSettingsRequest,
  ) => {
    try {
      return await $fetch<IBankingAccountSettingsResponse>(
        'v2/AccountSettings/upsert',
        {
          method: 'post',
          body: request,
          base: 'authApi',
          useBrandId: true,
          useAuthentication: true,
        },
      );
    } catch (e) {
      console.error(e);
    }
  };
  // locks
  const getUserLimits = async () => {
    try {
      return await $fetch<Partial<IUserGetLimitsType>>(
        'v3/Limits/user-limits',
        {
          useBrandId: true,
          useAuthentication: true,
          base: 'authApi',
          onResponse({ response }) {
            if (!!response?._data?.data?.restrictionResponses) {
              const activeUserLimits: IUserLimits[] =
                response?._data?.data?.restrictionResponses?.filter(
                  (limit: IUserLimits): boolean => limit.isActive === true,
                );
              if (activeUserLimits) {
                rgStore.setUserLimits(
                  activeUserLimits.sort(
                    (a, b) =>
                      a?.restrictionType?.priority -
                      b?.restrictionType?.priority,
                  ),
                );
              }
            }
            return response?._data?.data;
          },
        },
      );
    } catch (e) {
      console.error(e);
    }
  };
  const addUserLimit = async (request: IAddLimitRequest) => {
    // Add new limit to user account
    try {
      return $fetch<IAddLimitResponse>('v3/Limits/add-limit', {
        method: 'post',
        base: 'authApi',
        useBrandId: true,
        useAuthentication: true,
        body: { internalNote, ...request },
      });
    } catch (e) {
      console.error(e);
    }
  };
  const removeUserLimit = async (request: IRemoveLimitRequest) => {
    try {
      return await $fetch<IAddLimitResponse>('v3/Limits/remove-limit', {
        method: 'post',
        base: 'authApi',
        useBrandId: true,
        useAuthentication: true,
        body: {
          restrictionRemovalReason: 'user removal',
          internalNote,
          ...request,
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
  const updateUserLimit = async (request: IAddLimitRequest) => {
    try {
      return await $fetch<IAddLimitResponse>('v3/Limits/update-limit', {
        method: 'post',
        base: 'authApi',
        useBrandId: true,
        useAuthentication: true,
        body: { internalNote, ...request },
      });
    } catch (e) {
      console.error(e);
    }
  };
  const getWagerProgress = async () => {
    try {
      return await $fetch<IWagerProgress>('v3/Limits/wager-progress', {
        base: 'authApi',
        useBrandId: true,
        useAuthentication: true,
        onResponse({ response }) {
          if (!!response?._data?.data?.wagerLimits) {
            rgStore.setWagerDuration(response?._data?.data?.wagerLimits);
          }
          return response?._data?.data;
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
  const getBrandLimits = async () => {
    try {
      return await $fetch<Partial<IBrandGetLimitsType>>('v3/Limits', {
        base: 'authApi',
        useBrandId: true,
        useAuthentication: true,
        onResponse({ response }) {
          if (!!response._data?.data?.restrictionTypeResponses) {
            useCheckForBrandExclusions(
              response._data.data.restrictionTypeResponses,
            );
            rgStore.setBrandLimits(
              response._data?.data?.restrictionTypeResponses,
            );
          }
          return response?._data?.data;
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
  const getBrandLocks = async () => {
    try {
      return await $fetch('v2/locks', {
        base: 'authApi',
        useBrandId: true,
        useAuthentication: true,
        onResponse({ response }) {
          return response?._data?.data;
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
  const getUserLocks = async () => {
    try {
      return await $fetch('v2/Locks/user-locks', {
        base: 'authApi',
        useBrandId: true,
        useAuthentication: true,
        onResponse({ response }) {
          return response?._data?.data;
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
  const getUserRestrictions = async () => {
    try {
      return await $fetch('v2/restrictions/user-restrictions', {
        base: 'authApi',
        useBrandId: true,
        useAuthentication: true,
        onResponse({ response }) {
          return response?._data?.data;
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
  // rewards
  const updateRewardsBalance = async (request: IConvertToCashRequest) => {
    try {
      return await $fetch<IRewardsRedeemResponse>('balance', {
        method: 'post',
        body: request,
        base: 'rewards',
        useAuthentication: true,
        useBrandId: true,
        onResponse({ response }) {
          if (!!response._data) {
            const balance: Partial<IRewardsBalanceResponse> = {
              amount: response._data?.amount,
              lifeTimePointsEarned: response._data?.lifeTimePointsEarned,
              totalPointsConverted: response._data?.totalPointsConverted,
            };
            const periodSummary: Partial<IPeriod>[] =
              response._data?.yearSummary?.periodSummary;
            authStore.setRewardsBalance(balance);
            authStore.setSummaryYearConfig(periodSummary);
          }
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
  const getRewardsConfig = async (): Promise<IRewardsConfigResponse> => {
    return await $fetch<IRewardsConfigResponse>('configuration', {
      base: 'rewards',
      useAuthentication: true,
      useBrandId: true,
      onResponse({ response }) {
        if (!!response._data) authStore.setRewardsConfig(response._data);
      },
    });
  };
  const getRewardsSummary = async (): Promise<IRewardsRedeemResponse> => {
    try {
      return await $fetch('summary', {
        base: 'rewards',
        useAuthentication: true,
        useBrandId: true,
        onResponse({ response }) {
          if (!!response._data) {
            const balance: Partial<IRewardsBalanceResponse> = {
              amount: response._data?.amount,
              lifeTimePointsEarned: response._data?.lifeTimePointsEarned,
              totalPointsConverted: response._data?.totalPointsConverted,
            };
            const periodSummary: Partial<IPeriod>[] =
              response._data?.yearSummary?.periodSummary;
            authStore.setRewardsBalance(balance);
            authStore.setSummaryYearConfig(periodSummary);
          }
        },
      });
    } catch (e) {
      console.error(e);
    }
  };

  return {
    initPartialReg,
    verifyPartialRegOtp,
    requestPartialRegOtp,
    completePartialReg,
    register,
    login,
    handleLoginSession,
    getUserExtended,
    editUser,
    refreshToken,
    passwordResetLoggedIn,
    passwordResetInit,
    passwordResetConfirm,
    forgotLoginInit,
    forgotLoginAuthorise,
    fetchCommunicationsPreferences,
    updateCommunicationsPreferences,
    getBankingAccountSettings,
    upsertBankingAccountSettings,
    addMobileNumber,
    requestNewOtp,
    verifyOtp,
    getMobileNumberStatus,
    changePrimaryNumber,
    deleteMobileNumber,
    getBrandLimits,
    getUserLimits,
    addUserLimit,
    removeUserLimit,
    updateUserLimit,
    getWagerProgress,
    getBrandLocks,
    getUserLocks,
    getUserRestrictions,
    getRewardsConfig,
    updateRewardsBalance,
    getRewardsSummary,
  };
};
